import { ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation } from "@angular/core";
import { PageChangeEvent } from "@progress/kendo-angular-listview";
import { FileRestrictions, SelectEvent } from '@progress/kendo-angular-upload';
import { Observable } from "rxjs";
import { DestroyBase } from "../../../components/common/ng-destroy-base";
import { LocalizationService } from "../../../modules/app-settings/services/localization-service";
import { AppStorageService } from "../../../services/app-storage.service";
import { PicturesBaseService } from "../../../modules/project-valuation/services/pictures-base.service";

/**
 * Infobar (sidebar) that displays and handles actions to add/remove property images
 */
@Component({
  selector: 'picture-upload',
  templateUrl: './picture-upload.component.html',
  styleUrls: ['./picture-upload.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class PictureUploadComponent extends DestroyBase implements OnInit {
  /**
   * Data-ID: OID of the reference BO
   */
  private _dataId: number;

  /**
   * Sets the Data-ID
   */
  @Input() set dataId(id: number) {
    const refreshRequired = this._dataId > 0 && this._dataId !== id;
    this._dataId = id;
    if (refreshRequired) {
      this.refreshData();
    }
  }

  /**
   * Gets the Data-ID
   */
  get dataId() {
    return this._dataId;
  }

  /**
   * Reflist name for picture types
   */
  @Input() reflistName: string;

  /**
   * Required roles
   */
  @Input() requiredRoles: string[];

  /**
   * Default type for new pictures
   */
  @Input() defaultNewImageType: string;

  /**
   * Picture upload service
   */
  @Input() pictureService: PicturesBaseService;

  /**
   * Source for the list view
   */
  public listSource: any = [];

  /**
   * Paged pictures list
   */
  public pagedPictures: any = [];

  /**
   * Pictures view
   */
  public view: Observable<any>;

  /**
   * The current file
   */
  public currentFile: any;

  /**
   * The page size
   */
  pageSize = 4;

  /**
   * Skip info for paging
   */
  skip = 0;

  /**
   * Handles a page change
   * @param e
   */
  public onPageChange(e: PageChangeEvent): void {
    this.skip = e.skip;
    this.pageData();
  }

  /**
   * Gets the page data
   */
  private pageData(): void {
    this.pagedPictures = this.listSource.slice(
      this.skip,
      this.skip + this.pageSize
    );
  }

  /**
   * A list of image types
   */
  imageTypes: any[] = [];

  /**
   * The container element
   */
  containerElement: any = null;//formerly class="property-infobar-pictures-container"

  /**
   * The Kendo UI upload component
   */
  kendoUpload: any = null;

  /**
   * Upload restrictions: Only allow PNG and JPG
   */
  uploadRestrictions: FileRestrictions = {
    allowedExtensions: [".jpg", ".png"],
  };

  /**
   * Upload allowed flag
   */
  uploadAllowed = false;

  /**
   * Show loader flag
   */
  showLoader = false;

  /**
   * Constructor
   */
  constructor(private appStorage: AppStorageService, private localizationService: LocalizationService,

    /**
     * The change detector reference
     */
    private ref: ChangeDetectorRef) {
    super();
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.listSource.forEach((item) => (item.isFileSelected = false));
    if (this.requiredRoles) {
      this.uploadAllowed = this.appStorage.userIsInRole(this.requiredRoles);
    }

    this.localizationService.getRefList(this.reflistName).subscribe(response => {
      const sortColumn = "description";

      response.sort((a, b) => {//Sort ascending
        if (a[sortColumn] > b[sortColumn]) {
          return 1;
        }
        else if (a[sortColumn] < b[sortColumn]) {
          return -1;
        }
        return 0;
      });

      response.forEach((obj) => {
        obj.description = obj[sortColumn];
        this.imageTypes.push(obj);
      });

      this.refreshData();
    });
  }

  /**
   * Refreshes the data
   */
  refreshData(): void {
    this.showLoader = true;
    if (!this.dataId) {
      this.listSource = [];
      this.pageData();
      return;
    }

    this.getPicturesEndpoint(this.dataId).subscribe(data => {
      this.showLoader = false;
      this.listSource = data;
      this.fetchImagesBytes();
      this.skip = 0;
      this.pageData();
    });
  }

  /**
   * Gets the image's bytes
   */
  fetchImagesBytes(): void {
    this.listSource.forEach((image) => {
      this.getPictureBytesEndpoint(image.oid).subscribe((response) => {
        const blob = response;
        const reader = new FileReader();
        reader.readAsDataURL(blob); // convert blob to base64
        reader.onloadend = () => {
          setTimeout(() => {
            image.dataUrl = reader.result; // set as base64 string result
            image.isLoaded = true;
          });
        }
      });
    });
  }

  /**
   * Gets a picture type description for a picture
   * @param dataItem The picture
   * @returns Type description
   */
  getPictureTypeDescription(dataItem: any): string {
    const filtered = this.imageTypes.filter((obj) => {
      return obj.oid == dataItem.pictureTypeId;
    });

    return filtered.length ? filtered[0].description : "";
  }

  /**
   * Starts edit mode
   * @param dataItem Picture
   */
  startEditMode(dataItem: any): void {
    if (!this.uploadAllowed) return;
    dataItem.editMode = true;
  }

  /**
   * Ends edit mode
   * @param dataItem Picture
   */
  endEditMode(dataItem: any): void {
    dataItem.editMode = false;
  }

  /**
   * Handle changes
   * @param dataItem Picture
   */
  itemChanged(dataItem: any): void {
    dataItem.changed = true;
  }

  /**
   * Save a picture
   * @param dataItem Picture
   */
  savePicture(dataItem: any) {
    if (dataItem.saveInProgress) return;

    if (!dataItem.pictureTypeId) {
      confirm("Choose picture type!");
      return;
    }

    if (dataItem.oid == 0 && !dataItem.dataUrl) {
      confirm("Choose picture!");
      return;
    }

    this.showLoader = true;
    dataItem.saveInProgress = true;

    dataItem.filename = this.currentFile.name;
    dataItem.dataUrlFileName = this.currentFile.name;
    this.savePictureEndpoint(dataItem, this.dataId).subscribe((response) => {
      this.showLoader = false;

      if (response.errorMessage) {
        return;
      }

      if (dataItem.oid == 0) {//if new picture
        dataItem.oid = response.oid;
      }

      if (dataItem.url != response.url) {//if we changed picture type
        dataItem.url = response.url;
      }

      dataItem.changed = false;
      dataItem.saveInProgress = false;
      dataItem.editMode = false;

      this.refreshData();
    });
  }

  /**
   * Gets a file's sizes in KiB
   * @param sizeInBytes Size in bytes
   * @returns
   */
  formatFileSizeInKB(sizeInBytes: number): string {
    const sizeInKB = sizeInBytes / 1024;
    return sizeInKB.toFixed(2) + ' KB';
  }

  /**
   *
   * Delete a picture
   * @param dataItem Picture
   */
  deletePicture(dataItem: any) {
    if (!dataItem.oid) {
      //deleting item, that was only added in UI
      this.removeItemFromList(dataItem);
      return;
    }

    if (!confirm("Do you want to delete picture?")) {
      return;
    }

    this.showLoader = true;
    this.deletePictureEndpoint(dataItem.oid).subscribe((response) => {
      this.showLoader = false;
      if (response.errorMessage) {
        return;
      }

      this.removeItemFromList(dataItem);
    });
  }

  /**
   * Remove an item from the list
  */
  private removeItemFromList(dataItem: any): void {
    this.listSource = this.listSource.filter((item) => item !== dataItem);
    this.pageData();
  }

  /**
    * Add new picture item
    */
  addPictureItem(): void {
    const pictureItem: any = {
      oid: 0,
      pictureTypeId: +this.defaultNewImageType || 0,
      description: "",
      isLoaded: false,
      dataUrl: null//'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/x8AAwMCAO+ip1sAAAAASUVORK5CYII=' //1x1 transparent png
    };

    pictureItem.dataId = this.dataId;

    this.listSource = [pictureItem, ...this.listSource];
    this.skip = 0;
    this.pageData();
  }

  /**
   * File input changed
   * @param e SelectEvent
   * @param dataItem Picture
   */
  public fileInputChanged(e: SelectEvent, dataItem: any): void {
    e.preventDefault();

    if (e.files.length < 1) {
      // If no file selected, mark isFileSelected as false
      dataItem.isFileSelected = false;
      return;
    }
    this.currentFile = e.files[0];
    if (this.currentFile.validationErrors) return;

    const reader = new FileReader();

    reader.onload = (ev) => {
      dataItem.dataUrl = ev.target["result"];
      dataItem.isLoaded = true;
      // When a file is chosen, mark isFileSelected as true
      dataItem.isFileSelected = true;
      // Manually trigger change detection to update the view
      this.ref.detectChanges();
    };

    reader.readAsDataURL(this.currentFile.rawFile);
  }

  //Endpoints
  /**
   * Gets the API endpoint for getting pictures
   * @param oid OID
   * @returns API endpoint
   */
  getPicturesEndpoint(oid: number): Observable<any> {
    return this.pictureService.getPictures(oid);
  }

  /**
   * Gets the API endpoint for deleting pictures
   * @param oid OID
   * @returns API endpoint
   */
  deletePictureEndpoint(oid: number): Observable<any> {
    return this.pictureService.deletePicture(oid);
  }

  /**
   * Gets the API endpoint for saving pictures
   * @param oid OID
   * @returns API endpoint
   */
  savePictureEndpoint(pictureData: any, oid: number): Observable<any> {
    return this.pictureService.savepicture(pictureData, oid);
  }

  /**
   * Gets the API endpoint for getting picture bytes
   * @param oid OID
   * @returns API endpoint
   */
  getPictureBytesEndpoint(oid: number): Observable<any> {
    return this.pictureService.getPictureBytes(oid);
  }
}
